<template>
	<Modal
		:title="title"
		:closeOnOverlayClick="false"
		:visible="visible"
		@close="onCancel"
	>
		<template v-slot:body>
			<div class="titletec-app">
				<div class="titletec-app__state">
					<RadioGroup
						labelText="Application State"
						:options="stateOptions"
						v-model="currentState"
						@change="onStateChange"
						:isHorizontal="true"
						variant="leftLabel"
					/>
				</div>

				<div class="titletec-app__title-transfer">
					<SegmentedControl
						:options="options"
						v-model="currentOption"
						@change="onOptionChange"
					/>
				</div>

				<div class="titletec-app__fields-group">
					<DatePicker
						labelText="Date Sold"
						v-model="selectedDateSold"
						:datePickerType="datePickerType"
						:validators="soldDateValidators"
					/>
					<DatePicker
						labelText="Lien Date"
						v-model="selectedLienDate"
						v-if="isFinanceDeal"
						:datePickerType="datePickerType"
						:validators="lienDateValidators"
					/>

					<VehicleTypes
						v-if="hasRequirement(vehicleTypeRequirement)"
						v-model="currentVehicleType"
					/>
				</div>

				<div
					class="titletec-app__fields-group"
					v-if="hasRequirement(credentialsRequirement)"
				>
					<Input
						labelText="Username"
						v-model="currentUsername"
						autocomplete="off"
					/>
					<InputPassword
						labelText="Password"
						v-model="currentPassword"
						autocomplete="new-password"
					/>
				</div>

				<div class="titletec-app__error-txt" v-if="!credentialsValid">
					<ErrorFeedback
						:errorMessages="authenticationFailedMessage"
					/>
				</div>

				<SectionHeader
					class="titletec-app__transfer-head"
					sectionText="Transfer Vehicle"
					variant="textDivider"
					v-if="showTransfer"
				/>

				<div class="titletec-app__fields-group" v-if="showTransfer">
					<Input
						labelText="VIN"
						v-model="selectedVIN"
						:validators="vinValidators"
					/>
					<Input
						labelText="License Plate #"
						v-model="selectedLicenseNumber"
						:validators="licenseValidators"
					/>
				</div>
			</div>
		</template>

		<template v-slot:footer>
			<ButtonSet>
				<template #default>
					<ButtonClassic
						variant="primary"
						labelText="Submit"
						@click="onSubmit"
					/>
					<ButtonClassic
						variant="text"
						labelText="Cancel"
						@click="onCancel"
					/>
				</template>
			</ButtonSet>
		</template>
	</Modal>
</template>

<script>
import Modal from '@/components/shells/modal/modal.vue';
import SegmentedControl from '@/components/forms/selection-control/segmented-control/segmented-control.vue';
import DatePicker from '@/components/forms/date-picker/date-picker.vue';
import Input from '@/components/forms/input/input.vue';
import InputPassword from '@/components/forms/input/input-password.vue';
import VehicleTypes from './vehicle-types-dropdown.vue';
import RadioGroup from '@/components/forms/selection-control/radio/radio-group.vue';
import ButtonSet from '@/components/forms/button/button-set.vue';
import ButtonClassic from '@/components/forms/button/button-classic.vue';
import ErrorFeedback from '@/components/forms/shared-stateless-elements/error-feedback.vue';
import { mapGetters, mapActions } from 'vuex';
import viewMixin from '@/mixins/view-mixin';
import { SINGLE_DATE } from '@/components/forms/date-picker/date-picker-types';
import RequiredValidator from '@/validators/required-validator';
import SectionHeader from '@/components/typography/section-header/section-header.vue';

export default {
	name: 'TitleApplication',
	mixins: [viewMixin],
	components: {
		Modal,
		SegmentedControl,
		DatePicker,
		Input,
		InputPassword,
		VehicleTypes,
		RadioGroup,
		ButtonSet,
		ButtonClassic,
		ErrorFeedback,
		SectionHeader
	},
	data() {
		return {
			titleOption: 'title',
			transferOption: 'transfer',
			currentOption: this.titleOption,
			currentVehicleType: 'P',
			currentState: undefined,
			authenticationFailedMessage: [
				'Authentication failed, please verify your username/password.'
			],
			datePickerType: SINGLE_DATE,
			vendorName: undefined,
			selectedDateSold: undefined,
			selectedLienDate: undefined,
			selectedStateCode: undefined,
			selectedVIN: undefined,
			selectedLicenseNumber: undefined,
			currentPassword: undefined,
			currentUsername: undefined,
			vehicleTypeRequirement: 'VehicleType',
			credentialsRequirement: 'Credentials'
		};
	},
	props: {
		dealId: {
			type: Number,
			required: true
		},
		deal: {
			type: Object,
			required: true,
			default: () => {
				//just a mock for local dev
				return {};
			}
		},
		visible: {
			type: Boolean,
			default: false
		},
		integrationId: {
			type: Number
		}
	},
	async created() {
		if (this.integrationId) {
			await this.load();
		}
	},
	watch: {
		async integrationId(newValue) {
			if (newValue) {
				await this.load();
			}
		}
	},
	computed: {
		...mapGetters('deal', ['employeeIntegrations']),
		...mapGetters('principal', ['employeeId']),
		credentialsValidators() {
			let validators;

			if (this.hasRequirement(this.credentialsRequirement)) {
				validators = [new RequiredValidator()];
			}

			return validators;
		},
		vinValidators() {
			let validators;

			if (this.showTransfer) {
				validators = [new RequiredValidator()];
			}

			return validators;
		},
		licenseValidators() {
			let validators;

			if (this.showTransfer) {
				validators = [new RequiredValidator()];
			}

			return validators;
		},
		soldDateValidators() {
			//TODO: uncomment when DateRangeValidator complete in #79836
			//TODO: set maxDate to tommorrow 12am (midnight tonight)
			// return [
			// 	new DateRangeValidator({ maxDate: new Date() }),
			// 	new RequiredValidator()
			// ];
			return [];
		},
		lienDateValidators() {
			//TODO: uncomment when DateRangeValidator complete in #79836
			//TODO: set maxDate to tommorrow 12am (midnight tonight)
			// let validators;

			// if (this.isFinanceDeal) {
			// 	validators = [
			// 		new DateRangeValidator({ maxDate: new Date() }),
			// 		new RequiredValidator()
			// 	];
			// }

			// return validators;
			return [];
		},
		isFinanceDeal() {
			//TODO: update to get from this.deal
			return 'S';
		},
		selectedIntegration() {
			let integration = {};

			if (this.employeeIntegrations && this.employeeIntegrations.items) {
				integration = this.employeeIntegrations.items.find(
					i => i.stateCode === this.currentState
				);
			}

			return integration;
		},
		credentialsValid() {
			//TODO: this will come in the title api response
			return false;
		},
		showTransfer() {
			let result = false;

			if (this.currentOption === this.transferOption) {
				result = true;
			}

			return result;
		},
		options() {
			return [
				{ text: 'Title and Registration', value: this.titleOption },
				{ text: 'Transfer Plate', value: this.transferOption }
			];
		},
		title() {
			let integrationName = '';

			if (this.selectedIntegration) {
				integrationName = `${this.selectedIntegration.integrationName} Application`;
			}

			return integrationName;
		},
		states() {
			let stateArray = [];

			if (this.employeeIntegrations) {
				stateArray = this.employeeIntegrations.items.map(i => {
					return {
						state: i.state,
						stateCode: i.stateCode,
						requirements: i.requirements
					};
				});
			}

			return stateArray;
		},
		stateOptions() {
			let options;

			if (this.states) {
				options = this.states.map(s => {
					return { text: s.state, value: s.stateCode };
				});
			}

			return options;
		},
		hasRequirement() {
			return function(requiremnt) {
				let result = false;

				if (
					this.selectedIntegration &&
					this.selectedIntegration.requirements
				) {
					result =
						this.selectedIntegration.requirements.indexOf(
							requiremnt
						) >= 0;
				}

				return result;
			};
		}
	},
	methods: {
		...mapActions('deal', [
			'getEmployeeIntegrationsAction',
			'sendTitleRequestAction',
			'updateEmployeeIntegrationCredentialsAction'
		]),
		setDealData() {
			//TODO: set from this.deal
			this.selectedDateSold = new Date();
			this.selectedLienDate = new Date();
			this.selectedStateCode = 'FL';
			this.selectedVIN = '12345678';
			this.selectedLicenseNumber = 'ABC123';
		},
		setIntegrationData() {
			if (this.selectedIntegration) {
				this.currentUsername = this.selectedIntegration.username;
				this.currentPassword = this.selectedIntegration.password;
			}
		},
		async load() {
			this.setDealData();
			this.selectState(this.selectedStateCode);
			this.currentOption = this.titleOption;

			if (this.integrationId) {
				await this.getEmployeeIntegrations();
				this.setIntegrationData();
			}
		},
		async getEmployeeIntegrations() {
			await this.getEmployeeIntegrationsAction({
				companyId: this.companyId,
				integrationId: this.integrationId,
				employeeId: this.employeeId
			});

			this.$emit('ready');
		},
		onStateChange(val) {
			this.selectState(val);
		},
		onOptionChange(val) {
			this.currentOption = val;
		},
		selectState(stateCode) {
			let state;

			if (!stateCode && this.states && this.states.length > 0) {
				state = this.statesOptions[0];

				if (state) {
					stateCode = state.state;
				}
			}

			this.currentState = stateCode;
			this.setIntegrationData();
		},
		getTitleRequestData() {
			return {};
		},
		async onSubmit() {
			this.$emit('submit');

			await this.sendTitleRequestAction({
				companyId: this.companyId,
				titleRequest: this.getTitleRequestData()
			});

			//TODO: only call this if the submitted credentials were different than the original
			await this.updateEmployeeIntegrationCredentialsAction({
				companyId: this.companyId,
				integrationId: this.integrationId,
				employeeId: this.employeeId,
				username: this.currentUsername,
				password: this.currentPassword
			});

			this.$emit('complete');
		},
		onCancel() {
			this.$emit('cancel');
		}
	}
};
</script>

<style lang="scss" scoped>
.titletec-app {
	width: 540px;

	&__state,
	&__title-transfer {
		display: flex;
		justify-content: center;
	}

	&__state {
		margin-bottom: $gutter-lg;
	}

	&__fields-group {
		margin-top: $gutter-lg;
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: $gutter-xl;
	}

	&__error-txt {
		display: flex;
		justify-content: flex-start;
		margin-top: $gutter-sm;
	}

	&__transfer-head {
		margin-top: $gutter-xl;
	}
}

::v-deep .segmented-control__group-label {
	margin-bottom: 0;
}
</style>
